/**
 * 充值活动（Alice 页面 campaign 的复刻）：编辑充值赠送活动、用户端实时预览、发布历史。
 *
 * 背景：Alice 是自由金额充值，赠送按「金额 × 每元糖豆」算；本仓充值是 Stripe 买固定商品，所以去掉了
 *   「每 1 元兑换糖豆」与「充值预设金额」，新增「适用商品」；门槛按美元，固定奖励与上限直接用花瓣。
 *   配置整包保存，revision 乐观锁；每次发布往历史追加一行，已下的订单带着下单当时的配置快照。
 * 设计意图：版式、字段顺序、按钮与 Alice 一致；校验交给服务端（文案是给运营看的中文说明），前端只做输入联动：
 *   选了「按用户限制」的频率时自动生成活动批次，累计上限只在「每用户累计封顶」时可填，档位的赠送方式决定哪几个框可用。
 *   时间输入用本机时区，提交时转成带时区的 ISO（Alice 固定北京时间，这里没有单一时区）。
 * 关键约束：发布与「新批次」都先弹确认；409（别人刚改过）自动重新载入；历史行的「统计」跳到活动统计页并带上批次。
 */
import { useCallback, useEffect, useState } from 'react'
import { adminApi } from '@ap/api-client/admin'
import {
  campaignApi,
  type BonusType,
  type CampaignConfig,
  type CampaignHistoryItem,
  type CampaignMode,
  type CampaignStatus,
  type CampaignTier,
  type RewardMode,
  type StackMode,
} from '@ap/api-client/admin-campaign'
import { AG_campaign as G, errorTextC } from '@ap/i18n/admin-campaign'
import { useI18n } from '@ap/i18n'
import { ConfirmModal, fmtDateTime, navigate, useLoad, useToast } from '@ap/ui'
import { OpsNote } from '../../alice/kit.tsx'
import { fmtNum, fromLocalInput, toLocalInput } from '../../alice/kit-campaign.tsx'

interface TierRow {
  min: string
  type: BonusType
  rate: string
  fixed: string
  label: string
}

interface FormState {
  mode: CampaignMode
  name: string
  badge: string
  title: string
  description: string
  key: string
  startsAt: string
  endsAt: string
  rewardMode: RewardMode
  stackMode: StackMode
  cap: string
  skus: string[]
  tiers: TierRow[]
}

const EMPTY_FORM: FormState = {
  mode: 'regular',
  name: '',
  badge: '',
  title: '',
  description: '',
  key: '',
  startsAt: '',
  endsAt: '',
  rewardMode: 'per_order',
  stackMode: 'replace',
  cap: '0',
  skus: [],
  tiers: [],
}

const generatedKey = () => `campaign-${Date.now().toString(36)}`

function toRow(t: CampaignTier): TierRow {
  const fixed = t.bonus_type === 'fixed' ? t.bonus_value : (t.bonus_fixed ?? 0)
  const rate = t.bonus_type === 'fixed' ? 0 : t.bonus_value
  return { min: String(t.min_usd), type: t.bonus_type, rate: String(Number(rate.toFixed(2))), fixed: String(fixed), label: t.label }
}

function fromRow(r: TierRow): CampaignTier {
  const fixed = Number(r.fixed || 0)
  const rate = Number(r.rate || 0)
  return {
    min_usd: Number(r.min),
    bonus_type: r.type,
    bonus_value: r.type === 'fixed' ? fixed : rate,
    ...(r.type === 'combined' ? { bonus_fixed: fixed } : {}),
    label: r.label.trim(),
  }
}

function toForm(c: CampaignConfig): FormState {
  return {
    mode: c.mode,
    name: c.campaign_name || c.title || '',
    badge: c.badge,
    title: c.title,
    description: c.description,
    key: c.campaign_key,
    startsAt: toLocalInput(c.starts_at),
    endsAt: toLocalInput(c.ends_at),
    rewardMode: c.reward_mode || (c.fixed_bonus_cap > 0 ? 'capped_per_user' : 'per_order'),
    stackMode: c.stack_mode || 'replace',
    cap: String(c.fixed_bonus_cap || 0),
    skus: c.skus ?? [],
    tiers: (c.tiers ?? []).map(toRow),
  }
}

function collect(f: FormState): CampaignConfig {
  return {
    mode: f.mode,
    campaign_key: f.key.trim(),
    campaign_name: f.name.trim(),
    badge: f.badge.trim(),
    title: f.title.trim(),
    description: f.description.trim(),
    skus: f.skus,
    tiers: f.tiers.map(fromRow),
    starts_at: fromLocalInput(f.startsAt),
    ends_at: fromLocalInput(f.endsAt),
    reward_mode: f.rewardMode,
    stack_mode: f.stackMode,
    fixed_bonus_cap: f.rewardMode === 'capped_per_user' ? Math.floor(Number(f.cap || 0)) : 0,
  }
}

const STATUS_BADGE: Record<string, string> = { active: 'badge badge-ok', scheduled: 'badge badge-info', ended: 'badge', disabled: 'badge badge-off' }

export function Campaign() {
  const { t, locale } = useI18n()
  const toast = useToast()
  const { data, error, reload } = useLoad(async () => {
    const [overview, products] = await Promise.all([campaignApi.campaign(), adminApi.products()])
    return { overview, products: products.data }
  }, [])
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [revision, setRevision] = useState<number | null>(null)
  const [loadedNote, setLoadedNote] = useState<number | null>(null)
  const [confirmSave, setConfirmSave] = useState(false)
  const [confirmBatch, setConfirmBatch] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!data) return
    setForm(toForm(data.overview.campaign))
    setRevision(data.overview.campaign.revision)
    setLoadedNote(null)
  }, [data])

  const patch = useCallback((p: Partial<FormState>) => setForm((f) => ({ ...f, ...p })), [])
  const ensureKey = (next: Partial<FormState>) =>
    setForm((f) => {
      const merged = { ...f, ...next }
      if (merged.rewardMode !== 'per_order' && !merged.key.trim()) merged.key = generatedKey()
      return merged
    })
  const setTier = (i: number, p: Partial<TierRow>) =>
    setForm((f) => ({ ...f, tiers: f.tiers.map((r, j) => (j === i ? { ...r, ...p } : r)) }))
  const addTier = () =>
    setForm((f) => {
      const last = f.tiers[f.tiers.length - 1]
      return { ...f, tiers: [...f.tiers, { min: String(last ? Number(last.min || 0) + 50 : 50), type: 'fixed', rate: '0', fixed: '0', label: '' }] }
    })
  const removeTier = (i: number) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, j) => j !== i) }))
  const toggleSku = (sku: string) =>
    setForm((f) => ({ ...f, skus: f.skus.includes(sku) ? f.skus.filter((s) => s !== sku) : [...f.skus, sku] }))

  const overview = data?.overview
  const history = overview?.history ?? []
  const regularBase = history.find((h) => h.mode === 'regular' && h.status === 'active') ?? null
  const modeText = (m: CampaignMode) => t(G.campaign.modes[m].name)
  const statusText = (s: string) => (s in G.campaign.status ? t(G.campaign.status[s as keyof typeof G.campaign.status]) : s)

  const publish = async () => {
    if (revision == null) return
    setBusy(true)
    try {
      await campaignApi.saveCampaign(revision, collect(form))
      toast.ok(t(G.campaign.published))
      setConfirmSave(false)
      reload()
    } catch (e) {
      toast.error(errorTextC(e, locale))
      setConfirmSave(false)
      if (e && typeof e === 'object' && 'status' in e && (e as { status: number }).status === 409) reload()
    } finally {
      setBusy(false)
    }
  }

  const newBatch = () => {
    patch({ key: generatedKey() })
    setConfirmBatch(false)
    toast.ok(t(G.campaign.newBatchDone))
  }

  const loadHistory = (h: CampaignHistoryItem) => {
    setForm(toForm(h))
    setLoadedNote(h.revision)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // ---- 用户端预览
  const tierText = (tier: CampaignTier, kind: 'regular' | 'special') => {
    const label = tier.label ? ` · ${tier.label}` : ''
    const rate = tier.bonus_type === 'fixed' ? 0 : Number(tier.bonus_value || 0)
    const fixed = tier.bonus_type === 'fixed' ? Math.floor(Number(tier.bonus_value || 0)) : tier.bonus_type === 'combined' ? Math.floor(Number(tier.bonus_fixed || 0)) : 0
    const vars = { min: tier.min_usd.toLocaleString(), label, rate: Number(rate.toFixed(2)), n: fixed.toLocaleString() }
    if (rate > 0 && fixed > 0) return t(G.campaign.preview.tierCombined, vars)
    if (rate > 0) return t(G.campaign.preview.tierRate, vars)
    return t(kind === 'special' ? G.campaign.preview.tierFixedSpecial : G.campaign.preview.tierFixedRegular, vars)
  }
  const specialTiers = form.tiers.map(fromRow)
  const sections: { kind: 'regular' | 'special'; title: string; tag: string; tiers: CampaignTier[] }[] =
    form.mode === 'special' && form.stackMode === 'stack' && regularBase
      ? [
          { kind: 'regular', title: t(G.campaign.preview.regularTitle), tag: t(G.campaign.preview.regularTag), tiers: regularBase.tiers },
          { kind: 'special', title: t(G.campaign.preview.specialStackTitle), tag: form.badge.trim() || t(G.campaign.preview.specialTag), tiers: specialTiers },
        ]
      : [
          form.mode === 'special'
            ? { kind: 'special', title: t(G.campaign.preview.specialTitle), tag: t(G.campaign.preview.specialTag), tiers: specialTiers }
            : { kind: 'regular', title: t(G.campaign.preview.regularTitle), tag: t(G.campaign.preview.regularTag), tiers: specialTiers },
        ]
  const cap = Number(form.cap || 0)

  const periodText = (c: CampaignHistoryItem) =>
    `${c.starts_at ? fmtDateTime(c.starts_at, locale) : t(G.campaign.history.immediate)} → ${c.ends_at ? fmtDateTime(c.ends_at, locale) : t(G.campaign.history.ongoing)}`

  // 同一批次只有最新 revision 生效，旧版按时间算出来仍是 active，直接显示「进行中」会被误读成多份配置同时生效
  const latestByKey = new Map<string, number>()
  history.forEach((h) => latestByKey.set(h.campaign_key, Math.max(latestByKey.get(h.campaign_key) ?? 0, h.revision)))

  const chip = error
    ? t(G.campaign.chipFailed)
    : overview
      ? t(G.campaign.chip, {
          mode: overview.campaign.mode === 'none' ? t(G.campaign.modeShort.none) : modeText(overview.campaign.mode),
          status: statusText(overview.campaign_status),
        })
      : t(G.campaign.chipLoading)
  const effective = overview?.effective_campaign
  const disabledSection = form.mode === 'none' ? { opacity: 0.52 } : undefined

  return (
    <div className="panel active ops-panel">
      <div className="ops-page-head">
        <div>
          <div className="ops-eyebrow">{t(G.campaign.eyebrow)}</div>
          <h1>{t(G.campaign.title)}</h1>
          <p>{t(G.campaign.desc)}</p>
        </div>
        <div className="ops-head-chip">{chip}</div>
      </div>
      <OpsNote>{t(G.campaign.note)}</OpsNote>

      <div className="campaign-layout">
        <section className="campaign-editor">
          <div className="campaign-section">
            <div className="campaign-section-title">{t(G.campaign.modeTitle)}</div>
            <div className="campaign-section-copy">{t(G.campaign.modeCopy)}</div>
            <div className="campaign-mode-grid">
              {(['none', 'regular', 'special'] as const).map((m) => (
                <button key={m} className={`campaign-mode${form.mode === m ? ' active' : ''}`} type="button" onClick={() => patch({ mode: m })}>
                  <strong>{t(G.campaign.modes[m].name)}</strong>
                  <span>{t(G.campaign.modes[m].desc)}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="campaign-section" style={disabledSection}>
            <div className="campaign-section-title">{t(G.campaign.copyTitle)}</div>
            <div className="campaign-section-copy">{t(G.campaign.copyCopy)}</div>
            <div className="campaign-form-grid">
              <div className="campaign-field">
                <label>{t(G.campaign.f.name)}</label>
                <input maxLength={60} placeholder={t(G.campaign.f.namePh)} value={form.name} onChange={(e) => patch({ name: e.target.value })} />
              </div>
              <div className="campaign-field">
                <label>{t(G.campaign.f.badge)}</label>
                <input maxLength={20} value={form.badge} onChange={(e) => patch({ badge: e.target.value })} />
              </div>
              <div className="campaign-field">
                <label>{t(G.campaign.f.startsAt)}</label>
                <input type="datetime-local" value={form.startsAt} onChange={(e) => patch({ startsAt: e.target.value })} />
              </div>
              <div className="campaign-field">
                <label>{t(G.campaign.f.endsAt)}</label>
                <input type="datetime-local" value={form.endsAt} onChange={(e) => patch({ endsAt: e.target.value })} />
              </div>
              <div className="campaign-field">
                <label>{t(G.campaign.f.rewardMode)}</label>
                <select value={form.rewardMode} onChange={(e) => ensureKey({ rewardMode: e.target.value as RewardMode })}>
                  <option value="per_order">{t(G.campaign.f.perOrder)}</option>
                  <option value="once_per_user">{t(G.campaign.f.oncePerUser)}</option>
                  <option value="capped_per_user">{t(G.campaign.f.cappedPerUser)}</option>
                </select>
              </div>
              <div className="campaign-field">
                <label>{t(G.campaign.f.cap)}</label>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={form.cap}
                  disabled={form.rewardMode !== 'capped_per_user'}
                  onChange={(e) => ensureKey({ cap: e.target.value })}
                />
              </div>
              <div className="campaign-field">
                <label>{t(G.campaign.f.stackMode)}</label>
                <select value={form.stackMode} onChange={(e) => patch({ stackMode: e.target.value as StackMode })}>
                  <option value="stack">{t(G.campaign.f.stack)}</option>
                  <option value="replace">{t(G.campaign.f.replace)}</option>
                </select>
              </div>
              <div className="campaign-field full">
                <label>{t(G.campaign.f.key)}</label>
                <div style={{ display: 'flex', gap: 7 }}>
                  <input maxLength={64} placeholder={t(G.campaign.f.keyPh)} value={form.key} onChange={(e) => patch({ key: e.target.value })} />
                  <button
                    className="btn btn-secondary btn-sm"
                    type="button"
                    style={{ whiteSpace: 'nowrap' }}
                    onClick={() => (form.key ? setConfirmBatch(true) : newBatch())}
                  >
                    {t(G.campaign.f.newBatch)}
                  </button>
                </div>
              </div>
              <div className="campaign-field full">
                <label>{t(G.campaign.f.title)}</label>
                <input maxLength={60} value={form.title} onChange={(e) => patch({ title: e.target.value })} />
              </div>
              <div className="campaign-field full">
                <label>{t(G.campaign.f.description)}</label>
                <textarea maxLength={300} value={form.description} onChange={(e) => patch({ description: e.target.value })} />
              </div>
              <div className="campaign-field full">
                <label>{t(G.campaign.f.skus)}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(data?.products ?? []).filter((p) => p.active || form.skus.includes(p.sku)).map((p) => (
                    <label
                      key={p.sku}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, margin: 0, padding: '6px 10px', border: '1px solid var(--border-medium)', borderRadius: 7, background: 'var(--bg-input)', cursor: 'pointer', fontWeight: 500 }}
                    >
                      <input type="checkbox" style={{ width: 'auto' }} checked={form.skus.includes(p.sku)} onChange={() => toggleSku(p.sku)} />
                      <span>
                        {p.name} · {fmtNum(p.price_minor / 100, locale)} {p.currency.toUpperCase()}
                      </span>
                    </label>
                  ))}
                  {data && data.products.length === 0 && <span style={{ color: 'var(--text-tertiary)', fontSize: 12 }}>{t(G.campaign.f.skusEmpty)}</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="campaign-section" style={disabledSection}>
            <div className="campaign-section-title">{t(G.campaign.tiersTitle)}</div>
            <div className="campaign-section-copy">{t(G.campaign.tiersCopy)}</div>
            <div className="campaign-tier-list">
              {form.tiers.map((r, i) => (
                <div className="campaign-tier" key={i}>
                  <div className="campaign-field">
                    <label>{t(G.campaign.tier.minUsd)}</label>
                    <input type="number" min={1} max={10000} step={0.01} value={r.min} onChange={(e) => setTier(i, { min: e.target.value })} />
                  </div>
                  <div className="campaign-field">
                    <label>{t(G.campaign.tier.type)}</label>
                    <select value={r.type} onChange={(e) => setTier(i, { type: e.target.value as BonusType })}>
                      <option value="fixed">{t(G.campaign.tier.fixed)}</option>
                      <option value="rate">{t(G.campaign.tier.rate)}</option>
                      <option value="combined">{t(G.campaign.tier.combined)}</option>
                    </select>
                  </div>
                  <div className="campaign-field">
                    <label>{t(G.campaign.tier.rateField)}</label>
                    <input type="number" min={0} max={100} step={0.01} value={r.rate} disabled={r.type === 'fixed'} onChange={(e) => setTier(i, { rate: e.target.value })} />
                  </div>
                  <div className="campaign-field">
                    <label>{t(r.type === 'combined' ? G.campaign.tier.fixedExtra : G.campaign.tier.fixedField)}</label>
                    <input type="number" min={0} step={1} value={r.fixed} disabled={r.type === 'rate'} onChange={(e) => setTier(i, { fixed: e.target.value })} />
                  </div>
                  <div className="campaign-field">
                    <label>{t(G.campaign.tier.label)}</label>
                    <input maxLength={20} value={r.label} placeholder={t(G.campaign.tier.labelPh)} onChange={(e) => setTier(i, { label: e.target.value })} />
                  </div>
                  <button className="campaign-tier-remove" type="button" title={t(G.campaign.removeTier)} onClick={() => removeTier(i)}>
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button className="btn btn-secondary btn-sm" type="button" style={{ marginTop: 10 }} onClick={addTier}>
              {t(G.campaign.addTier)}
            </button>
          </div>

          <div className="campaign-savebar">
            <span className="campaign-version">
              {loadedNote != null ? t(G.campaign.loaded, { n: loadedNote }) : revision != null ? t(G.campaign.revision, { n: revision }) : t(G.campaign.revisionNone)}
            </span>
            <button className="btn btn-primary" type="button" disabled={revision == null || busy} onClick={() => setConfirmSave(true)}>
              {busy ? t(G.campaign.publishing) : t(G.campaign.publish)}
            </button>
          </div>
        </section>

        <aside className="campaign-preview-shell">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <strong style={{ fontSize: 12 }}>{t(G.campaign.preview.title)}</strong>
            <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{t(G.campaign.preview.live)}</span>
          </div>
          <div className={`campaign-live-card ${form.mode}`}>
            {form.mode === 'none' ? (
              <>
                <span className="campaign-live-badge">{t(G.campaign.preview.baseBadge)}</span>
                <h2 className="campaign-live-title">{t(G.campaign.preview.baseTitle)}</h2>
                <p className="campaign-live-desc">{t(G.campaign.preview.baseDesc)}</p>
              </>
            ) : (
              <>
                <span className="campaign-live-badge">{form.badge.trim() || t(form.mode === 'special' ? G.campaign.preview.specialBadge : G.campaign.preview.regularBadge)}</span>
                <h2 className="campaign-live-title">{form.title.trim() || t(G.campaign.preview.fillTitle)}</h2>
                <p className="campaign-live-desc">{form.description.trim() || t(G.campaign.preview.fillDesc)}</p>
                <div className="campaign-benefit-sections">
                  {sections.map((s) => (
                    <section key={s.kind} className={`campaign-benefit-section${s.kind === 'special' ? ' special-benefit' : ''}`}>
                      <div className="campaign-benefit-title">
                        <strong>{s.title}</strong>
                        <span>{s.tag}</span>
                      </div>
                      <div className="campaign-live-tiers">
                        {s.tiers
                          .filter((x) => x.min_usd > 0 && (x.bonus_value > 0 || (x.bonus_fixed ?? 0) > 0))
                          .sort((a, b) => a.min_usd - b.min_usd)
                          .map((x, i) => (
                            <span key={i} className="campaign-live-tier">
                              {tierText(x, s.kind)}
                            </span>
                          ))}
                      </div>
                    </section>
                  ))}
                  {form.rewardMode === 'once_per_user' && <div className="campaign-policy-note">{t(G.campaign.preview.onceNote)}</div>}
                  {form.rewardMode === 'capped_per_user' && cap > 0 && (
                    <div className="campaign-policy-note">{t(G.campaign.preview.cappedNote, { cap: cap.toLocaleString() })}</div>
                  )}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>

      <section className="ops-section-card">
        <div className="ops-section-head">
          <div>
            <div className="ops-section-title">{t(G.campaign.history.title)}</div>
            <div className="ops-section-copy">{t(G.campaign.history.copy)}</div>
          </div>
          <span className="campaign-version">
            {t(G.campaign.history.effective, {
              name: effective && effective.mode !== 'none' ? effective.campaign_name || effective.title : t(G.campaign.history.noEffective),
              n: effective?.revision ?? 0,
            })}
          </span>
        </div>
        <div className="table-wrap" style={{ border: 0, borderRadius: 0 }}>
          <div className="table-inner">
            <table>
              <thead>
                <tr>
                  <th>{t(G.campaign.history.cols.revision)}</th>
                  <th>{t(G.campaign.history.cols.campaign)}</th>
                  <th>{t(G.campaign.history.cols.mode)}</th>
                  <th>{t(G.campaign.history.cols.period)}</th>
                  <th>{t(G.campaign.history.cols.reward)}</th>
                  <th>{t(G.campaign.history.cols.stack)}</th>
                  <th>{t(G.campaign.history.cols.status)}</th>
                  <th>{t(G.campaign.history.cols.publishedAt)}</th>
                  <th>{t(G.campaign.history.cols.actions)}</th>
                </tr>
              </thead>
              <tbody>
                {!data ? (
                  <tr>
                    <td colSpan={9} className="empty">
                      {error ? errorTextC(error, locale) : t(G.common.loading)}
                    </td>
                  </tr>
                ) : history.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="empty">
                      {t(G.campaign.history.empty)}
                    </td>
                  </tr>
                ) : (
                  history.map((h) => {
                    const superseded = h.revision < (latestByKey.get(h.campaign_key) ?? 0) && (h.status === 'active' || h.status === 'scheduled')
                    const status: CampaignStatus | 'superseded' = superseded ? 'superseded' : h.status
                    return (
                      <tr key={h.revision}>
                        <td className="mono">r{h.revision}</td>
                        <td>
                          <strong>{h.campaign_name || h.title || t(G.campaign.history.unnamed)}</strong>
                          <div style={{ fontSize: 9, color: 'var(--text-tertiary)' }}>{h.campaign_key}</div>
                        </td>
                        <td>{t(G.campaign.modeShort[h.mode])}</td>
                        <td style={{ fontSize: 10 }}>{periodText(h)}</td>
                        <td>{t(h.reward_mode === 'once_per_user' ? G.campaign.history.oncePerUser : h.reward_mode === 'capped_per_user' ? G.campaign.history.cappedPerUser : G.campaign.history.perOrder)}</td>
                        <td>{t(h.stack_mode === 'stack' ? G.campaign.history.stack : G.campaign.history.replace)}</td>
                        <td>
                          <span className={status === 'superseded' ? 'badge badge-off' : (STATUS_BADGE[status] ?? 'badge')}>{statusText(status)}</span>
                        </td>
                        <td style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{h.published_at ? fmtDateTime(h.published_at, locale) : t(G.common.dash)}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => navigate(`/campaign-stats?key=${encodeURIComponent(h.campaign_key)}`)}>
                            {t(G.campaign.history.stats)}
                          </button>{' '}
                          <button className="btn btn-secondary btn-sm" type="button" onClick={() => loadHistory(h)}>
                            {t(G.campaign.history.load)}
                          </button>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <ConfirmModal
        open={confirmSave}
        title={t(G.campaign.publishConfirmTitle)}
        confirmLabel={t(G.campaign.publish)}
        busy={busy}
        onConfirm={() => void publish()}
        onClose={() => setConfirmSave(false)}
      >
        {t(G.campaign.publishConfirm, { mode: modeText(form.mode) })}
      </ConfirmModal>
      <ConfirmModal
        open={confirmBatch}
        title={t(G.campaign.newBatchConfirmTitle)}
        onConfirm={newBatch}
        onClose={() => setConfirmBatch(false)}
      >
        {t(G.campaign.newBatchConfirm)}
      </ConfirmModal>
    </div>
  )
}
